/**
 * The cartoon look: a picture redrawn in flat colours, for people who would
 * rather not show their real photographs on the card. Made on the device, from
 * the picture's preview copy - since 2026-10-10 the photograph never goes to a
 * server, so neither does this (until then it was made on our own server, and
 * never by an AI service: the owner's choice, 2026-09-27).
 *
 * The recipe, tried against the owner's own photographs until faces stayed
 * recognisable and busy backgrounds stayed clean:
 *   1. smooth at a third of the size with an edge-preserving filter, four
 *      passes - flat colour, sharp outlines;
 *   2. back to full size, then 20 colours with no dithering - the flat areas;
 *   3. a median filter and a slight blur to take the stair-steps off the
 *      edges, then a little more colour.
 * The heavy steps run in cartoon-worker.ts. Drawn outlines were tried and left
 * out: on grass, gravel and skin they scribble.
 *
 * The result is kept beside the photograph's own copies, the same size and
 * shape, so a zone can switch between the two looks and keep its move / zoom / turn.
 */
import { decode, drawScaled, fit, SIZES, toJpeg, type PreparedFile, type SizeKind } from './pictures.ts';

const SHRINK = 3;

function inWorker(job: Record<string, unknown> & { rgba: Uint8ClampedArray }): Promise<Uint8ClampedArray> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./cartoon-worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<Uint8ClampedArray>) => { resolve(e.data); worker.terminate(); };
    worker.onerror = (e) => { reject(new Error(e.message || 'The cartoon could not be drawn.')); worker.terminate(); };
    worker.postMessage(job, [job.rgba.buffer]);
  });
}

const pixels = (c: HTMLCanvasElement) => c.getContext('2d')!.getImageData(0, 0, c.width, c.height).data;
function put(c: HTMLCanvasElement, rgba: Uint8ClampedArray) {
  c.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(rgba), c.width, c.height), 0, 0);
}

/** The cartoon copies of a picture (thumb and preview), from its preview copy. */
export async function cartoonOf(preview: Blob): Promise<PreparedFile[]> {
  const img = await decode(preview);
  const w = img.naturalWidth, h = img.naturalHeight;
  const sw = Math.max(1, Math.round(w / SHRINK)), sh = Math.max(1, Math.round(h / SHRINK));
  const small = drawScaled(img, w, h, sw, sh);
  put(small, await inWorker({ op: 'smooth', rgba: pixels(small), width: sw, height: sh,
    passes: 4, radius: 3, spatialSigma: 1.8, rangeSigma: 24 }));
  const big = drawScaled(small, sw, sh, w, h);
  put(big, await inWorker({ op: 'finish', rgba: pixels(big), width: w, height: h,
    colours: 20, saturation: 1.15, brightness: 1.03 }));
  const out: PreparedFile[] = [];
  for (const kind of ['thumb', 'preview'] as SizeKind[]) {
    const s = fit(w, h, SIZES[kind]);
    const c = s.width === w && s.height === h ? big : drawScaled(big, w, h, s.width, s.height);
    out.push({ kind, blob: await toJpeg(c), ...s });
  }
  small.width = 0;
  big.width = 0;
  return out;
}
